// ═══════════════════════════════════════════════════════════════
// RollCall — Create Event
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Paper, Group, Button, TextInput, Select, Textarea, NumberInput,
} from '@mantine/core';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';

const EVENT_TYPES = [
  { value: 'shift', label: 'Shift' },
  { value: 'meal', label: 'Meal' },
  { value: 'training', label: 'Training' },
  { value: 'briefing', label: 'Briefing' },
  { value: 'custom', label: 'Custom' },
];

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snack', label: 'Snack' },
];

export default function EventCreate() {
  const navigate = useNavigate();
  const { incidents, loadIncidents } = useAuthStore();
  const { createEvent } = useRollCallStore();

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    incidentId: '',
    eventType: '',
    mealType: '',
    name: '',
    eventDate: new Date().toISOString().slice(0, 10),
    startTime: '',
    endTime: '',
    location: '',
    autoCheckoutHours: '' as string | number,
    notes: '',
  });

  useEffect(() => { loadIncidents(); }, []);

  const activeIncidents = incidents.filter(i => i.status === 'active' || i.status === 'monitoring');

  const autoName = (type: string, mealType: string, date: string) => {
    const label = type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
    const meal = mealType ? ` - ${mealType.charAt(0).toUpperCase() + mealType.slice(1)}` : '';
    const d = date ? ` - ${new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '';
    return `${label}${meal}${d}`;
  };

  const updateField = (field: string, value: unknown) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if ((field === 'eventType' || field === 'mealType' || field === 'eventDate') &&
          (!prev.name || prev.name === autoName(prev.eventType, prev.mealType, prev.eventDate))) {
        next.name = autoName(
          field === 'eventType' ? String(value) : next.eventType,
          field === 'mealType' ? String(value) : next.mealType,
          field === 'eventDate' ? String(value) : next.eventDate,
        );
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.incidentId || !form.eventType || !form.name.trim()) {
      notifications.show({ title: 'Error', message: 'Please fill required fields', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      await createEvent({
        incidentId: form.incidentId,
        eventType: form.eventType,
        mealType: form.eventType === 'meal' ? form.mealType : '',
        name: form.name.trim(),
        eventDate: form.eventDate,
        startTime: form.startTime,
        endTime: form.endTime,
        location: form.location,
        notes: form.notes,
        autoCheckoutHours: form.autoCheckoutHours ? String(form.autoCheckoutHours) : '',
      });
      notifications.show({ title: 'Created', message: 'Event created', color: 'green' });
      navigate('/rollcall/events');
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to create', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">New Event</Title>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/rollcall/events')}>Cancel</Button>
      </Group>

      <Paper p="lg" radius="sm" withBorder maw={640}>
        <Group grow mb="md">
          <Select label="Incident" required placeholder="Select incident"
            data={activeIncidents.map(i => ({ value: String(i.id), label: i.name }))}
            value={form.incidentId} onChange={v => updateField('incidentId', v ?? '')} />
          <Select label="Event Type" required placeholder="Select type" data={EVENT_TYPES}
            value={form.eventType} onChange={v => updateField('eventType', v ?? '')} />
        </Group>

        {form.eventType === 'meal' && (
          <Select label="Meal Type" placeholder="Select meal" data={MEAL_TYPES}
            value={form.mealType} onChange={v => updateField('mealType', v ?? '')} mb="md" />
        )}

        <TextInput label="Event Name" required value={form.name}
          onChange={e => setForm(p => ({ ...p, name: e.currentTarget.value }))} mb="md" />

        <Group grow mb="md">
          <TextInput label="Date" type="date" required value={form.eventDate}
            onChange={e => updateField('eventDate', e.currentTarget.value)} />
          <TextInput label="Start Time" type="time" value={form.startTime}
            onChange={e => setForm(p => ({ ...p, startTime: e.currentTarget.value }))} />
        </Group>

        <Group grow mb="md">
          <TextInput label="End Time" type="time" value={form.endTime}
            onChange={e => setForm(p => ({ ...p, endTime: e.currentTarget.value }))} />
          <TextInput label="Location" value={form.location}
            onChange={e => setForm(p => ({ ...p, location: e.currentTarget.value }))} />
        </Group>

        <NumberInput label="Auto-Checkout (hours)" placeholder="e.g. 12" min={1} max={72}
          value={form.autoCheckoutHours} onChange={v => setForm(p => ({ ...p, autoCheckoutHours: v ?? '' }))} mb="md" />

        <Textarea label="Notes" value={form.notes}
          onChange={e => setForm(p => ({ ...p, notes: e.currentTarget.value }))} mb="lg" />

        <Group justify="flex-end">
          <Button variant="light" onClick={() => navigate('/rollcall/events')}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} leftSection={<IconCheck size={14} />}>
            Create Event
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
